import { useEffect, useState } from "react";
import AuiIcon from "../../components/AuiDropdown/AuiIcon";
import AuiButton from "../../components/Buttons/Buttons";
import Headings from "../../components/Headings/Headings";
import "./UserConsentsView.scss";
import {
  fetchConsentRequestById,
  fetchFeedBackDocuments,
  updateConsentStatus,
} from "../../services/training-session.service";
import { useNavigate, useParams } from "react-router-dom";
import { codeToTextMapper } from "../../utils/constants/app-constants";
import Modals from "../../components/Modals/Modals";
import { useDispatch } from "react-redux";
import { showAlert } from "../../store/alertSlice";

const UserConsentsView = () => {
  const { id } = useParams<{ id: string }>();
  const [consentData, setConsentData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDownloadBtnLoading, setIsDownloadBtnLoading] = useState(false);
  const fetchConsentDataAndDocuments = async (consentId: any) => {
    if (!consentId || consentId === -1) {
      return;
    }
    setIsLoading(true);
    try {
      const getConsentDetail = fetchConsentRequestById(consentId);
      const getSupportDocumentList = fetchFeedBackDocuments({
        userId: consentId,
        documentType: "USER SUPPORTING DOCUMENTS",
      })
      const [consentResponse, documentResponse] = await Promise.all([
        getConsentDetail,
        getSupportDocumentList,
      ]);
      if (consentResponse?.error?.status === 404 || documentResponse?.error?.status === 404) {
        navigate("/page-not-found");
        return;
      }
      setConsentData({
        ...consentResponse,
        documentUrl: documentResponse?.data?.userDocuments[0]?.documentUrl
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateConsent = async (id: number, isApproved: boolean) => {
    const consentResponse = await updateConsentStatus(id, isApproved);
    if (consentResponse.status === 200) {
      navigate("/user-consent-request");
      dispatch(
        showAlert({ message: consentResponse.data.userConcent, type: "success" })
      );
    }
  }

  const isValidId = (id: string | undefined): boolean => {
    return id !== undefined && /^\d+$/.test(id);
  };

  useEffect(() => {
    if (!isValidId(id)) {
      navigate("/page-not-found");
      return;
    }
    const consentId = parseInt(id ?? "", 10);
    fetchConsentDataAndDocuments(consentId);
  }, [id]);

  const downloadSupportDoc = () => {
    setIsDownloadBtnLoading(true)
    const link = document.createElement("a");
    link.setAttribute("href", consentData.documentUrl);
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
    setIsDownloadBtnLoading(false);
  }, 1000); 
  };

  return (
    <div className="container mt-4 mb-5 UserConsentsViewConst">
            {isLoading ? (
        <div className="aui-loader loaderHeight">
          <aui-loader type="content"></aui-loader>
        </div>
      ) : (
      <>
      <aui-breadcrumb
        previouspages='[{"title":"All Courses","url":"/course-listing"}, {"title":"Consent Requests","url":"/user-consent-request"}]'
        currentpage="User Details"
      ></aui-breadcrumb>
      <Headings size="h1" className="pb-2 my-2 border-bottom">
        User Details
      </Headings>
        <div className="border-bottom my-3 userConsentsViewDec">
          <div className="row mb-0 mb-lg-4">
            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-lg-5">
              <label htmlFor="firstName">First Name</label>
              <p>{consentData?.firstName || "-"}</p>
            </div>
            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-lg-5">
              <label htmlFor="lastName">Last Name</label>
              <p>{consentData?.lastName || "-"}</p>
            </div>
            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-lg-5">
              <label htmlFor="email">Email</label>
              <p>{consentData?.email || "-"}</p>
            </div>
            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-lg-5">
              <label htmlFor="HospitalName">Primary Workplace</label>
              <p>{consentData?.hospitalName || "-"}</p>
            </div>
            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-lg-5">
              <label htmlFor="CadreName">Cadre</label>
              <p>{consentData?.cadreName || "-"}</p>
            </div>
            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-lg-5">
              <label htmlFor="role">Role</label>
              <p>{codeToTextMapper[consentData?.role]}</p>
            </div>

            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-lg-5">
              <label htmlFor="status">Status</label>
              <p>
                {consentData?.status
                  ? consentData.status[0].toUpperCase() +
                    consentData.status.slice(1).toLowerCase()
                  : "-"}
              </p>
            </div>
            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-lg-5">
              <label htmlFor="firstName">Supporting Documents</label>
              {isDownloadBtnLoading ? (
                  <aui-button
                    buttontitle="Download"
                    variant="secondary"
                    loading
                    size="medium"
                    buttonclass="custBtn py-2"
                  />
                ) : (
                  <button
                className="custBtn py-2 mb-4"
                onClick={downloadSupportDoc}
              >
                <AuiIcon iconClass="download" />
                <span className="pl-2">Documents</span>
              </button>
                )}
            </div>
          </div>
        </div>
      <div className="row d-flex justify-content-between align-items-center mt-4">
        <div className="col-3 col-md-6 col-lg-6">
          <p className="backButton">
            <aui-icon icon="arrowleft" svgwidth="21" svgheight="21"></aui-icon>
            <AuiButton
              variant="general-text-link"
              buttontitle="Back"
              buttonid="consentBackNav"
              size="large"
              anchorlinkurl="/user-consent-request"
              buttonclass="customClasstest"
            ></AuiButton>
          </p>
        </div>
        {consentData?.status === "PENDING" && (
          <div className="col-9 col-md-6 col-lg-6 d-flex justify-content-end">
            <span className="mr-3">
              <Modals
                id="rejectConsentId"
                modalButtonTitle="Reject"
                modalsize="auimodal-sm"
                headingAlign="center"
                modalTitle="Reject Consent"
                footerAlign="center"
                buttonTitle="Reject"
                modalid="rejectConsentModal"
                variant="secondary"
                action={() => updateConsent(parseInt(id ?? ''), false)}
              >
                <p className="text-center font-400 h8 pb-2">
                  Are you sure you want to Reject Consent?
                </p>
              </Modals>
            </span>
            <Modals
              modalButtonTitle="Approve"
              modalsize="auimodal-sm"
              headingAlign="center"
              modalTitle="Approve Consent"
              footerAlign="right"
              buttonTitle="Approve"
              modalid="approveConsentModal"
              variant="primary"
              action={() => updateConsent(parseInt(id ?? ''), true)}
            >
              <p className="text-center font-400 h8 pb-2">
                Are you sure you want to Approve Consent?
              </p>
            </Modals>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};
export default UserConsentsView;
